import { AlertTriangle } from "lucide-react";

import { cn } from "@/lib/cn";

/**
 * QueryErrorState — an explicit "backend unreachable" panel shown when a data
 * query has FAILED (query.isError), in place of fabricated zeros.
 *
 * Key it off request failure, never off a value being zero: a real $0.00 / 0%
 * with a successful response is a valid state and must still render normally.
 */
export function QueryErrorState({
  what = "data",
  title = "Can't reach the backend",
  detail,
  onRetry,
  className,
}: {
  what?: string;
  /**
   * Headline. Override for degraded-but-reachable states (e.g. the backend
   * answered but its data store is unavailable) — "Can't reach the backend"
   * is misleading there.
   */
  title?: string;
  /** Optional extra line, e.g. the backend's own degraded note. */
  detail?: string;
  onRetry?: () => void;
  className?: string;
}) {
  return (
    <div
      role="alert"
      className={cn(
        "flex flex-col items-start gap-2 rounded-lg border border-red-500/40 bg-red-500/10 p-4 text-sm text-red-300",
        className,
      )}
    >
      <div className="flex items-center gap-2 font-medium">
        <AlertTriangle className="h-4 w-4 shrink-0" />
        {title}
      </div>
      <p className="text-red-300/80">
        This {what} is unavailable right now — showing nothing rather than a
        misleading value. Check system status, then retry.
      </p>
      {detail ? <p className="text-xs text-red-300/60">{detail}</p> : null}
      {onRetry ? (
        <button
          type="button"
          onClick={onRetry}
          className="rounded border border-red-500/40 px-2.5 py-1 text-xs font-semibold text-red-200 transition-colors hover:bg-red-500/15"
        >
          Retry
        </button>
      ) : null}
    </div>
  );
}
